import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  applyQuoteGeneration,
  createAutoProjectQuote,
  createInvoiceFromQuote,
  createQuote,
  deleteQuote,
  finalizeQuoteInvoice,
  generateQuote,
  getAutoProjectDocuments,
  getInvoices,
  getQuote,
  getQuoteOffers,
  getQuotes,
  importQuoteSerge,
  pushQuoteToPennylane,
  setQuoteStatus,
  syncQuote,
  updateQuote,
} from '../services/api.js'

const LIST_KEY = ['quotes']
const quoteKey = (id) => ['quote', Number(id)]

/** Liste des devis (menu Contrats > Devis) et état de la connexion Pennylane. */
export function useQuotes() {
  return useQuery({ queryKey: LIST_KEY, queryFn: getQuotes })
}

/**
 * Un devis et toutes ses actions. Tant que l'agent prépare une proposition, le devis est relu
 * toutes les 3 s (la proposition apparaît dans quote.generation).
 */
export function useQuote(id) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: quoteKey(id),
    queryFn: () => getQuote(id),
    enabled: Boolean(id),
    refetchInterval: (q) => (['queued', 'running'].includes(q.state.data?.generation?.status) ? 3000 : false),
  })

  // Chaque action renvoie le devis à jour.
  const onSuccess = (quote) => {
    if (quote?.id) queryClient.setQueryData(quoteKey(quote.id), quote)
    queryClient.invalidateQueries({ queryKey: LIST_KEY })
  }
  const action = (fn) => useMutation({ mutationFn: fn, onSuccess })

  return {
    ...query,
    quote: query.data ?? null,
    update: action(updateQuote),
    generate: useMutation({
      mutationFn: generateQuote,
      onSuccess: () => queryClient.invalidateQueries({ queryKey: quoteKey(id) }),
    }),
    applyGeneration: action(applyQuoteGeneration),
    importSerge: action(importQuoteSerge),
    push: action(pushQuoteToPennylane),
    sync: action(syncQuote),
    setStatus: action(setQuoteStatus),
    createInvoice: action(createInvoiceFromQuote),
    finalizeInvoice: action(finalizeQuoteInvoice),
  }
}

/** Offres (projets Serge) pour lesquelles faire un devis, avec leurs prospects. */
export function useQuoteOffers(enabled = true) {
  return useQuery({ queryKey: ['quoteOffers'], queryFn: getQuoteOffers, enabled, staleTime: 30000 })
}

export function useCreateQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createQuote,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
      queryClient.invalidateQueries({ queryKey: ['quoteOffers'] })
    },
  })
}

export function useDeleteQuote() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteQuote,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: LIST_KEY }),
  })
}

/** Factures Pennylane d'une entreprise (menu Contrats > Factures), page par page. */
export function useInvoices(companyId = null) {
  return useInfiniteQuery({
    queryKey: ['invoices', companyId],
    queryFn: ({ pageParam }) => getInvoices(pageParam, companyId),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  })
}

/** Devis et contrat d'un projet auto (boutons du détail projet). */
export function useAutoProjectDocuments(projectId) {
  const queryClient = useQueryClient()
  const key = ['autoProjectDocuments', projectId]
  const query = useQuery({ queryKey: key, queryFn: () => getAutoProjectDocuments(projectId), enabled: Boolean(projectId) })
  const createQuote = useMutation({
    mutationFn: () => createAutoProjectQuote(projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: key })
      queryClient.invalidateQueries({ queryKey: LIST_KEY })
    },
  })

  return { ...query, quote: query.data?.quote ?? null, contract: query.data?.contract ?? null, createQuote }
}
